import React, { FC, useEffect, useRef, useMemo } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import clsx from 'clsx'
import { createShowFromBottomAnimation } from 'src/helpers/animations'
import { preventOrphans } from 'src/helpers/typography'

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger)
}

export interface IStatItemProps {
  value: string | number
  suffix?: string
  label: string
  hasRedDot?: boolean
  className?: string
}

interface IDigitReelConfig {
  target: number
  isFirst: boolean
  sequence: number[]
}

// Precise optical width per digit in Palanquin Dark Semibold
// Ensures first, second, and last digits (especially wide 0s) have full unclipped boundaries
const getDigitWidth = (digit: number): string => {
  switch (digit) {
    case 1:
      return '0.48em'
    case 0:
      return '0.62em'
    case 4:
    case 6:
    case 8:
    case 9:
      return '0.54em'
    case 2:
    case 3:
    case 5:
    case 7:
    default:
      return '0.52em'
  }
}

// 2 full cycles of all 10 digits (20 steps) from target to target.
// UP: Ascending order: T -> T+1 -> T+2 ... -> T
const generateUpCycle = (target: number): number[] => {
  const seq: number[] = []
  for (let i = 0; i <= 20; i++) {
    seq.push((target + i) % 10)
  }
  return seq
}

// DOWN: Descending order so that translating from -Y to 0 moves downwards
// while passing sequentially: T -> T+1 -> T+2 ... -> T.
// Top (index 0) is T; bottom (index 20) is T.
const generateDownCycle = (target: number): number[] => {
  const seq: number[] = []
  for (let i = 20; i >= 0; i--) {
    seq.push((target + i) % 10)
  }
  return seq
}

const StatItem: FC<IStatItemProps> = ({
  value,
  suffix = '',
  label,
  hasRedDot = false,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const reelRefs = useRef<(HTMLSpanElement | null)[]>([])

  const valueStr = String(value).trim()
  const match = valueStr.match(/^(\d+)(.*)$/)
  const digitsStr = match ? match[1] : valueStr.replace(/\D/g, '') || '0'
  const displaySuffix = (match && match[2] ? match[2] : '') + (suffix || '')

  const digits = useMemo(() => digitsStr.split(''), [digitsStr])

  const digitConfigs = useMemo<IDigitReelConfig[]>(() => {
    return digits.map((digitChar, index) => {
      const target = parseInt(digitChar, 10) || 0
      const isFirst = index === 0

      return {
        target,
        isFirst,
        sequence: isFirst ? generateUpCycle(target) : generateDownCycle(target),
      }
    })
  }, [digits])

  useEffect(() => {
    if (!containerRef.current) return
    const animTl = gsap.timeline()
    createShowFromBottomAnimation(animTl, containerRef.current)
  }, [])

  useEffect(() => {
    if (!containerRef.current || digitConfigs.length === 0) return

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (prefersReducedMotion) return

    const tweens: gsap.core.Tween[] = []
    const totalItems = 21 // 20 steps + 1 terminal duplicate
    const travelDistancePercent = ((totalItems - 1) / totalItems) * 100

    digitConfigs.forEach((config, idx) => {
      const el = reelRefs.current[idx]
      if (!el) return

      if (config.isFirst) {
        // UP: Starts at 0 (target), scrolls UPWARDS to -travelDistancePercent (target),
        // then cleanly settles to 0 where the identical target sits.
        const tween = gsap.fromTo(
          el,
          { yPercent: 0 },
          {
            yPercent: -travelDistancePercent,
            duration: 3.8,
            ease: 'expo.inOut',
            scrollTrigger: {
              trigger: containerRef.current,
              start: 'top 78%',
              once: true,
            },
            onComplete: () => {
              gsap.set(el, { yPercent: 0 })
            },
          }
        )
        tweens.push(tween)
      } else {
        // DOWN: Starts at -travelDistancePercent (target), scrolls DOWNWARDS to 0 (target).
        // Identical targets (like 00) stay in complete synchronous lockstep passing 00, 11, 22...
        const tween = gsap.fromTo(
          el,
          { yPercent: -travelDistancePercent },
          {
            yPercent: 0,
            duration: 3.8,
            ease: 'expo.inOut',
            scrollTrigger: {
              trigger: containerRef.current,
              start: 'top 78%',
              once: true,
            },
          }
        )
        tweens.push(tween)
      }
    })

    return () => {
      tweens.forEach((tween) => {
        tween.scrollTrigger?.kill()
        tween.kill()
      })
    }
  }, [digitConfigs])

  return (
    <div
      ref={containerRef}
      className={clsx('flex flex-col items-start text-left opacity-0 w-fit', className)}
    >
      <div className={clsx('relative inline-flex items-baseline self-start')}>
        {hasRedDot && (
          <div
            className={clsx(
              'hidden md:block absolute -top-1 -right-2 w-2.5 h-2.5 bg-primary rounded-full'
            )}
          />
        )}

        {/* Accessible screen-reader text */}
        <span className={clsx('sr-only')}>
          {digitsStr}
          {displaySuffix}
        </span>

        {/* Visual Animated Numbers with Baseline-Aligned Suffix */}
        <div
          aria-hidden="true"
          className={clsx(
            'inline-flex items-baseline font-palanquinDark font-semibold tracking-tighter leading-none text-on-surface',
            'text-display-mega sm:text-heading-h6 md:text-heading-h6 lg:text-heading-h6 xl:text-heading-h2'
          )}
        >
          {/* Digit Reels */}
          <div className={clsx('inline-flex items-baseline leading-none')}>
            {digitConfigs.map((config, idx) => (
              <span
                key={idx}
                className={clsx(
                  'inline-block relative overflow-hidden align-baseline select-none',
                  '[text-box-trim:both] [text-box-edge:cap_alphabetic]'
                )}
                style={{
                  height: '1.05em',
                  lineHeight: '1.05em',
                  width: getDigitWidth(config.target),
                  paddingRight: idx === digitConfigs.length - 1 ? '0.02em' : undefined,
                }}
              >
                <span
                  ref={(el) => {
                    reelRefs.current[idx] = el
                  }}
                  className={clsx('flex flex-col will-change-transform w-full')}
                >
                  {config.sequence.map((num, nIdx) => (
                    <span
                      key={nIdx}
                      className={clsx(
                        'flex items-center justify-center select-none font-palanquinDark font-semibold',
                        '[text-box-trim:both] [text-box-edge:cap_alphabetic]'
                      )}
                      style={{
                        height: '1.05em',
                        lineHeight: '1.05em',
                        width: '100%',
                      }}
                    >
                      {num}
                    </span>
                  ))}
                </span>
              </span>
            ))}
          </div>

          {/* Suffix: half size of the large number, positioned as superscript (sup) */}
          {displaySuffix && (
            <sup
              className={clsx(
                'self-start leading-none font-semibold select-none text-on-primary-subtle',
                'ml-pico mt-[0.24em] text-[0.5em]',
                '[text-box-trim:both] [text-box-edge:cap_alphabetic]'
              )}
            >
              {displaySuffix}
            </sup>
          )}
        </div>
      </div>

      <span
        className={clsx(
          'block text-left font-palanquin font-bold text-on-surface-subtle [text-wrap:pretty]',
          'mt-extra-small md:mt-base',
          'text-body-large md:text-title-small lg:text-title-small xl:text-title-medium'
        )}
      >
        {typeof label === 'string' ? preventOrphans(label) : label}
      </span>
    </div>
  )
}

export default StatItem
